<script setup>
  import { Modal } from 'bootstrap'
  import { onMounted, useTemplateRef, ref, computed, watch, onUnmounted } from "vue"
  import { filter, groupBy, chunk, map, find, truncate, words } from "lodash"
  import { io } from 'socket.io-client'

  const props = defineProps({
    sessionId: {
      type: String,
      required: true
    }
  })

  // Flag to track connection status
  const isConnected = ref(false)

  defineEmits(['reset'])

  import token from "@/components/token.vue"

  import characterJSON from '@/assets/characters.json'
  import { cards as cardsData } from '@/assets/cards.js'

  // Resolve the Socket.IO server URL.
  const socketUrl = window.location.origin

  const socket = io( socketUrl, {
    maxHttpBufferSize: 25 * 1024 * 1024,
    autoConnect: true,
  } )

  const modalRef = useTemplateRef( "modalRef" )
  const customNoteModalRef = useTemplateRef( "customNoteModalRef" )
  const customNoteTextRef = useTemplateRef( "customNoteText" )
  const customEditionModalRef = useTemplateRef( "customEditionModalRef" )
  const customEditionUrlRef = useTemplateRef( "customEditionUrlInput" )
  const timerDurationModalRef = useTemplateRef( "timerDurationModalRef" )
  const numberSelectionModalRef = useTemplateRef( "numberSelectionModalRef" )
  const cameraModalRef = useTemplateRef( "cameraModalRef" )
  let myModal
  let customNoteModal
  let customEditionModal
  let timerDurationModal
  let numberSelectionModal
  let cameraModal

  const customNote = ref('')
  const showCustomNote = ref(false)

  const customEditionUrl = ref('')
  const customEditionData = ref(null)
  const customEditionName = ref('Custom')
  const customEditionAuthor = ref('')
  const isCustomEdition = computed(() => !!customEditionData.value)

  // Search state
  const isSearching = ref(false)
  const showSearchDropdown = ref(false)
  const selectedSearchIndex = ref(-1)
  const selectedSearchItem = ref(null)
  const searchResults = ref([])

  // Local storage keys
  const CUSTOM_EDITION_URL_KEY = 'botc-custom-edition-url'
  const CUSTOM_EDITION_NAME_KEY = 'botc-custom-edition-name'
  const CUSTOM_EDITION_AUTHOR_KEY = 'botc-custom-edition-author'
  const SELECTED_EDITION_KEY = 'botc-selected-edition'

  const selectedCards = ref( [] )
  const selectedCharacters = ref( [] )

  const selectedEdition = ref( "tb" )

  // Persist and restore selected edition
  // Valid standard editions
  const STANDARD_EDITIONS = ['tb', 'bmr', 'snv']

  const cards = ref( cardsData )

  const teams = ref( ['townsfolk', 'outsider', 'minion', 'demon'] )

  const editions = ref( [ 'tb', 'bmr', 'snv' ] )

  const selectedAlignment = ref( null )

  onMounted( async () => {
    myModal = new Modal( modalRef.value )
    customNoteModal = new Modal( customNoteModalRef.value )
    customEditionModal = new Modal( customEditionModalRef.value )
    timerDurationModal = new Modal( timerDurationModalRef.value )
    numberSelectionModal = new Modal( numberSelectionModalRef.value )
    cameraModal = new Modal( cameraModalRef.value, { backdrop: 'static', keyboard: false, focus: false } )

    customNoteModalRef.value.addEventListener( 'shown.bs.modal', () => {
      if ( customNoteTextRef.value ) {
        customNoteTextRef.value.select()
      }
    } )

    customEditionModalRef.value.addEventListener( 'shown.bs.modal', () => {
      if ( customEditionUrlRef.value ) {
        customEditionUrlRef.value.removeAttribute( 'title' )
        customEditionUrlRef.value.select()
      }
    } )

    // Restore selected edition deterministically
    let savedEdition = null
    try {
      savedEdition = localStorage.getItem(SELECTED_EDITION_KEY)
    } catch (e) {
      console.warn('Could not read saved edition from localStorage', e)
    }

    const savedUrl = (() => {
      try { return localStorage.getItem(CUSTOM_EDITION_URL_KEY) } catch { return null }
    })()
    const savedName = (() => {
      try { return localStorage.getItem(CUSTOM_EDITION_NAME_KEY) } catch { return null }
    })()
    const savedAuthor = (() => {
      try { return localStorage.getItem(CUSTOM_EDITION_AUTHOR_KEY) } catch { return null }
    })()
    if (savedAuthor) customEditionAuthor.value = savedAuthor

    if (savedEdition === 'tb' || savedEdition === 'bmr' || savedEdition === 'snv') {
      selectedEdition.value = savedEdition
      // Do not auto-load custom on standard selection
    } else if (savedEdition === 'custom') {
      if (savedUrl) {
        customEditionUrl.value = savedUrl
        if (savedName) customEditionName.value = savedName
        const ok = await loadCustomEditionFromUrl(savedUrl)
        if (ok) {
          selectedEdition.value = 'custom'
        } else {
          console.warn('Saved custom edition failed to load; falling back to tb')
          selectedEdition.value = 'tb'
          try { localStorage.setItem(SELECTED_EDITION_KEY, 'tb') } catch {}
        }
      } else {
        // No URL to load custom, fallback
        selectedEdition.value = 'tb'
        try { localStorage.setItem(SELECTED_EDITION_KEY, 'tb') } catch {}
      }
    } else {
      // No valid saved selection
      selectedEdition.value = 'tb'
      try { localStorage.setItem(SELECTED_EDITION_KEY, 'tb') } catch {}
    }

    // Set up connection event handlers
    socket.on('connect', () => {
      isConnected.value = true
      // Join the session room as a control pad
      console.log( `Joining session: ${props.sessionId} as control pad` )
      socket.emit( 'joinSession', props.sessionId, 'controlPad' )
    })

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error)
      isConnected.value = false
    })

    socket.on('disconnect', () => {
      isConnected.value = false
    })

    // If already connected, join the session
    if (socket.connected) {
      isConnected.value = true
      console.log( `Joining session: ${props.sessionId} as control pad` )
      socket.emit( 'joinSession', props.sessionId, 'controlPad' )
    }

    // Force connection when tab is reactivated from sleep/background
    document.addEventListener('visibilitychange', handleVisibilityChange)
  } )

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' && socket.disconnected) {
      console.log('Tab active, reconnecting socket...')
      socket.connect()
    }
  }

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  function showModal() {
    myModal.show()
  }

  function showCustomNoteModal() {
    customNoteModal.show()
  }

  function hideCustomNoteModal() {
    customNoteModal.hide()
  }

  function showCustomEditionModal() {
    customEditionModal.show()
  }

  function hideCustomEditionModal() {
    customEditionModal.hide()
    customEditionUrl.value = ''
    selectedSearchItem.value = null
    searchResults.value = []
    showSearchDropdown.value = false
  }

  function showTimerDurationModal() {
    timerDurationModal.show()
  }

  function hideTimerDurationModal() {
    timerDurationModal.hide()
  }

  function showNumberSelectionModal() {
    numberSelectionModal.show()
  }

  function hideNumberSelectionModal() {
    numberSelectionModal.hide()
  }

  // Helper functions for Custom Script handling (URL normalization and search)
  function isProbablyUrl(str) {
    return /^https?:\/\//i.test(str)
  }
  function isJsonUrl(str) {
    return /\/api\/scripts\/\d+\/json\/?.*$/i.test(str)
  }
  function isScriptPageUrl(str) {
    return /\/scripts\/\d+\/?.*$/i.test(str)
  }
  function isSearchUrl(str) {
    return /\?search=/i.test(str)
  }
  function extractScriptId(str) {
    const m1 = str.match(/\/api\/scripts\/(\d+)\//i)
    if (m1) return m1[1]
    const m2 = str.match(/\/scripts\/(\d+)\//i)
    if (m2) return m2[1]
    return null
  }
  function extractSearchQuery(str) {
    try {
      const u = new URL(str)
      return u.searchParams.get('search') || ''
    } catch {
      return ''
    }
  }
  function toJsonUrlFromId(id) {
    return `https://www.botcscripts.com/api/scripts/${id}/json/`
  }
  function isLocalJsonUrl(str) {
    return /^\/api\/botc\/script\/\d+\/json/.test(str)
  }
  function onSearchBlur() {
    setTimeout(() => { showSearchDropdown.value = false }, 200)
  }
  function setSearchIndex(idx) {
    selectedSearchIndex.value = idx
  }

  async function performSearch(term) {
    try {
      isSearching.value = true
      showSearchDropdown.value = true
      selectedSearchIndex.value = -1
      selectedSearchItem.value = null
      // Use resolved socketUrl so dev and prod both work
      const base = socketUrl
      const resp = await fetch(`${base}/api/botc/search?q=${encodeURIComponent(term)}`)
      const json = await resp.json()
      searchResults.value = Array.isArray(json.results) ? json.results : []
    } catch (e) {
      console.error('Search failed', e)
      searchResults.value = []
    } finally {
      isSearching.value = false
    }
  }

  function onSearchKeydown(event) {
    if (!showSearchDropdown.value || searchResults.value.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      selectedSearchIndex.value = (selectedSearchIndex.value + 1) % searchResults.value.length
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      selectedSearchIndex.value = (selectedSearchIndex.value - 1 + searchResults.value.length) % searchResults.value.length
    } else if (event.key === 'Enter') {
      if (selectedSearchIndex.value >= 0) {
        const item = searchResults.value[selectedSearchIndex.value]
        chooseSearchItem(item)
      }
    } else if (event.key === 'Escape') {
      showSearchDropdown.value = false
    }
  }

  function chooseSearchItem(item) {
    selectedSearchItem.value = item
    customEditionUrl.value = item?.jsonUrl || ''
    customEditionName.value = item?.name || 'Custom'
    customEditionAuthor.value = item?.author || ''
    showSearchDropdown.value = false
  }

  // Debounce timer for custom edition search input
  let searchDebounceTimer = null

  // Watch input for debounced search
  watch(customEditionUrl, (newVal) => {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)

    // A local proxy URL was set by chooseSearchItem — don't trigger a new search
    if (isLocalJsonUrl(newVal)) {
      showSearchDropdown.value = false
      return
    }

    selectedSearchItem.value = null

    // If it's a search URL, extract the term; if it's a JSON or script URL, no search
    let term = newVal
    if (isProbablyUrl(newVal)) {
      if (isSearchUrl(newVal)) {
        term = extractSearchQuery(newVal)
      } else {
        showSearchDropdown.value = false
        searchResults.value = []
        return
      }
    }

    // Only search for 3+ chars
    term = (term || '').trim()
    if (term.length < 3) {
      showSearchDropdown.value = false
      searchResults.value = []
      return
    }

    searchDebounceTimer = setTimeout(() => {
      performSearch(term)
    }, 400)
  })

  // Function to load custom edition data from a URL
  async function loadCustomEditionFromUrl( url ) {
    try {
      const response = await fetch( url )
      if ( !response.ok ) {
        throw new Error( `HTTP error! status: ${response.status}` )
      }

      const data = await response.json()

      // Check if the data has the expected format
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('Invalid JSON format: expected an array')
      }

      // Find the _meta object
      const metaObj = find(data, { id: '_meta' })
      if (metaObj && metaObj.name) {
        customEditionName.value = metaObj.name
      }
      if (metaObj && metaObj.author) {
        customEditionAuthor.value = metaObj.author
      }

      // Set in-memory custom data; selection handled elsewhere
      customEditionData.value = data

      // Persist the custom URL/name/author
      localStorage.setItem( CUSTOM_EDITION_URL_KEY, url )
      localStorage.setItem( CUSTOM_EDITION_NAME_KEY, customEditionName.value )
      localStorage.setItem( CUSTOM_EDITION_AUTHOR_KEY, customEditionAuthor.value )

      return true
    }
    catch ( error ) {
      console.error( 'Error fetching custom edition:', error )
      return false
    }
  }

  async function applyCustomEdition() {
    let url = customEditionUrl.value.trim()
    if (!url) return

    // Normalize any botcscripts.com URL to go through our local proxy
    if (isProbablyUrl(url)) {
      const id = extractScriptId(url)
      if (id) url = `/api/botc/script/${id}/json`
    }

    try {
      const success = await loadCustomEditionFromUrl( url )
      if ( success ) {
        selectedEdition.value = 'custom'
        hideCustomEditionModal()
      }
      else {
        alert( `Error loading custom edition. Please check the URL and try again.` )
      }
    }
    catch ( error ) {
      console.error( 'Error in applyCustomEdition:', error )
      alert( `Error loading custom edition: ${error.message}` )
    }
  }

  function clearCustomEdition() {
    customEditionData.value = null
    customEditionName.value = 'Custom'
    customEditionAuthor.value = ''
  }

  // Helper function to safely emit events only when connected
  function safeEmit( event, data ) {
    if ( isConnected.value ) {
      socket.emit( event, data )
    }
    else {
      console.log( `Not connected, skipping emit: ${event}` )
    }
  }

  function applyCustomNote() {
    showCustomNote.value = true
    safeEmit( 'customNoteUpdated', { show: true, text: customNote.value } )
    closeCustomNote()
  }

  function closeCustomNote() {
    showCustomNote.value = false
    hideCustomNoteModal()
  }

  function resetWithoutEdition() {
    selectedCards.value = []
    selectedCharacters.value = []
    selectedTimer.value = null
    selectedAlignment.value = null
    selectedNumber.value = null
    showCustomNote.value = false
    customNote.value = ''
    // capturedImage.value = null
    imageRotation.value = 0
    safeEmit( 'resetTimer' )
    safeEmit( 'resetAlignment' )
    safeEmit( 'customNoteUpdated', { show: false, text: '' } )
    safeEmit( 'numberSelected', null )
    safeEmit( 'resetImage' )
  }

  function hideModal() {
    myModal.hide()
    resetWithoutEdition()
  }

  const truncatedCustomName = computed(() => {
    const name = customEditionName.value || ''
    const initials = words(name).map( ( w )  => w?.[0] || '').join('')
    return initials.toLowerCase()
  })

  function underscoreToSpace( str ) {
    return str.replace( "_", " " );  // "helloworld"
  }


  const characters = computed( () => {
    if ( isCustomEdition.value && customEditionData.value ) {
      // For custom edition, filter characters based on IDs in the custom edition data
      const characterIds = customEditionData.value
        .filter( item => item.id !== '_meta' )
        .map( item => item.id )

      // Find all characters with matching IDs
      const customCharacters = characterIds.map( id => find( characterJSON, { id } ) )
        .filter( char => char !== undefined ) // Remove any undefined entries

      return groupBy( customCharacters, 'team' )
    }
    else {
      // For standard editions, filter by edition as before
      const byEdition = filter( characterJSON, { edition: selectedEdition.value } )
      return groupBy( byEdition, 'team' )
    }
  } )

  function transformPath( inputPath, type ) {
    const filename = inputPath.substring( inputPath.lastIndexOf( '/' ) + 1 )
    return `/tokens/${type}/${filename}`
  }

  function groupList( list, size ) {
    return chunk( list, size )
  }

  // Persist selected edition to localStorage
  watch(selectedEdition, (newVal) => {
    try {
      if (STANDARD_EDITIONS.includes(newVal)) {
        localStorage.setItem(SELECTED_EDITION_KEY, newVal)
      } else if (newVal === 'custom') {
        if (isCustomEdition.value && customEditionData.value) {
          localStorage.setItem(SELECTED_EDITION_KEY, 'custom')
        } else {
          // Not a valid custom yet; don’t persist
        }
      } else {
        localStorage.removeItem(SELECTED_EDITION_KEY)
      }
    } catch (e) {
      console.warn('Could not persist selected edition', e)
    }
  })

  watch( selectedCards, ( newCards ) => {
    safeEmit( 'cardsSelected', map( newCards, 'name' ) )
  } )

  watch( selectedCharacters, async( newCharacters, oldCharacters ) => {
    safeEmit( 'charactersSelected', map( newCharacters, 'id' ) )
  } )

  watch( selectedAlignment, async( newAlignment, oldAlignment ) => {
    safeEmit( 'alignmentSelected', newAlignment )
  } )

  const selectedTimer = ref( null )
  const selectedNumber = ref( null )

  // Photo capture state
  const capturedImage = ref( null )
  const imageRotation = ref( 0 )

  function startTimer( minutes ) {
    selectedTimer.value = minutes
    safeEmit( 'startTimer', minutes )
    hideTimerDurationModal()
  }

  function selectNumber( number ) {
    selectedNumber.value = number
    safeEmit( 'numberSelected', number )
    hideNumberSelectionModal()
  }

  function showCameraModal() {
    cameraModal.show()
  }

  function hideCameraModal() {
    cameraModal.hide()
  }

  function rotateImage(direction) {
    // Adjust rotation by 90 degrees in the specified direction
    if (direction === 'left') {
      imageRotation.value = (imageRotation.value - 90) % 360
    }
    else if (direction === 'right') {
      imageRotation.value = (imageRotation.value + 90) % 360
    }

    // If rotation is negative, convert to positive equivalent
    if (imageRotation.value < 0) {
      imageRotation.value = 360 + imageRotation.value
    }

    // Send the updated rotation to the display
    if (capturedImage.value) {
      safeEmit('imageRotated', imageRotation.value)
    }
  }

  // File input capture flow (supports both camera capture and choosing from device files)
  const fileInputCameraRef = useTemplateRef('fileInputCamera')
  const fileInputGalleryRef = useTemplateRef('fileInputGallery')

  function triggerCameraCapture() {
    if (fileInputCameraRef.value) fileInputCameraRef.value.click()
  }

  function triggerGalleryPick() {
    if (fileInputGalleryRef.value) fileInputGalleryRef.value.click()
  }

  function onFileChange(event) {
    const file = event.target?.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onloadend = () => {
      capturedImage.value = reader.result
      imageRotation.value = 0
      // On some mobile browsers, returning from the camera can inadvertently close the modal.
      // Ensure the modal remains visible after selecting/taking a photo.
      try {
        if (cameraModal) {
          // Defer to next tick so Bootstrap can settle focus after the picker closes
          setTimeout(() => cameraModal.show(), 0)
        }
      } catch (e) {
        console.warn('Could not re-show camera modal after file selection:', e)
      }
    }
    reader.readAsDataURL(file)
  }

  function saveImage() {
    if (!capturedImage.value) return
    const a = document.createElement('a')
    a.href = capturedImage.value
    a.download = 'photo.jpg'
    a.click()
  }

  function sendImageToDisplay() {
    if (capturedImage.value) {
      console.log('Sending image to display')
      safeEmit('imageCaptured', {
        image: capturedImage.value,
        rotation: imageRotation.value
      })
    }
  }
</script>

<template lang="pug">
  span.connection-dot( :class="isConnected ? 'connected' : 'disconnected'" )
  .row.mb-2
    .col-12.d-flex.justify-content-center.flex-wrap.control-buttons-container
      .btn-group.me-md-3
        template( v-for="edition in editions" )
          input.btn-check( type="radio" :id="edition" name="edition" v-model="selectedEdition" :value="edition" @change="clearCustomEdition" )
          label.btn.btn-outline-light.p-3( :for="edition" ) {{ edition }}
        input.btn-check( type="radio" id="custom" name="edition" v-model="selectedEdition" value="custom" v-if="isCustomEdition" )
        label.btn.btn-outline-light.p-3( for="custom" v-if="isCustomEdition" ) {{ truncatedCustomName }}
        button.btn.btn-outline-light.p-3( @click="showCustomEditionModal( )" ) url
        .btn.btn-outline-light.p-3( @click="$emit( 'reset' )" ) home

      .btn-group
        input.btn-check( type="radio" id="alignment-good" name="alignment" v-model="selectedAlignment" value="good" )
        label.btn.btn-outline-light.text-success.p-3( for="alignment-good" )
          | Good
        input.btn-check( type="radio" id="alignment-evil" name="alignment" v-model="selectedAlignment" value="evil" )
        label.btn.btn-outline-light.text-danger.p-3( for="alignment-evil" )
          | Evil
        input.btn-check( type="radio" id="alignment-none" name="alignment" v-model="selectedAlignment" :value="null" )
        label.btn.btn-outline-light.p-3( for="alignment-none" ) None

      .btn-group.ms-md-3
        button.btn.btn-outline-light.p-3( @click="showTimerDurationModal( )" ) timer {{selectedTimer}}
        button.btn.btn-outline-light.p-3( @click="showNumberSelectionModal( )" ) number {{selectedNumber}}
        button.btn.btn-outline-light.p-3( @click="showCameraModal( )" ) photo

  .row.mb-3
    .col-12.d-flex.justify-content-center
      .btn-group.mx-auto.cards.flex-wrap
        template( v-for="card in cards" )
          input.btn-check( type="checkbox" :id="card.name" name="card" v-model="selectedCards" :value="card" )
          label.btn.btn-outline-light.px-2.py-4.align-content-center( :for="card.name" ) {{ underscoreToSpace( card.name ) }}
        button.btn.btn-outline-light.px-2.py-4( @click="showCustomNoteModal( )" ) custom note

  template( v-for="team in teams" )
    .row.mb-2
      .col-12.d-flex.justify-content-center
        div( style="max-width: max-content;" )
          .btn-group.flex-wrap.d-flex.justify-content-center
            template( v-for="character in characters[team]" )
              input.btn-check( type="checkbox" :id="character.name" name="character" v-model="selectedCharacters" :value="character" )
              label.btn.p-0.flex-grow-0( :for="character.name" )
                token( :center-image="transformPath( character.image, character.team )"
                       :name="character.name"
                       :id="character.id"
                       :alignment="selectedAlignment"
                       :showAlignment="false"
                       :selected="selectedCharacters.some( c => c.id === character.id )" )

  .row.mb-2
    .col-12.text-center
      .btn.btn-outline-light.btn-lg.px-3.px-md-5.py-2.py-md-3.me-1.me-md-2( @click="showModal( )" ) Show
      .btn.btn-outline-light.btn-lg.px-3.px-md-5.py-2.py-md-3( @click="resetWithoutEdition( )" ) Reset

  .modal.fade( ref="modalRef" )
    .modal-dialog.modal-fullscreen
      .modal-content.text-center
        .modal-body
          template( v-if="selectedCards.length > 0" )
            .alert.alert-light.text-uppercase( v-for="card in selectedCards" :key="card.name" )
              | {{ card.text }}

          template( v-if="selectedCharacters.length <= 2" )
            .image-container.mb-3( v-for="character in selectedCharacters" )
              token.mx-auto( :center-image="transformPath( character.image, character.team )"
                     :name="character.name"
                     :id="character.id"
                     size="large"
                     :alignment="selectedAlignment"
                     :showAlignment="true" )

          template( v-else )
            .row.g-0
              .col( v-for="character in selectedCharacters" )
                token.mx-auto( :center-image="transformPath( character.image, character.team )"
                               :name="character.name"
                               :id="character.id"
                               size="large"
                               :alignment="selectedAlignment"
                               :showAlignment="true" )
        .modal-footer.p-1
          button.btn.btn-sm.btn-link( @click="hideModal( )" )
            | Close

  .modal.fade( ref="customNoteModalRef" )
    .modal-dialog.modal-dialog-centered.modal-lg
      .modal-content
        .modal-header
          h5.modal-title Custom Note
        .modal-body
          .form-group
            textarea.form-control( id="customNoteText" ref="customNoteText" v-model="customNote" rows="5" )
        .modal-footer
          button.btn.btn-primary( @click="applyCustomNote( )" ) Show
          button.btn.btn-link( @click="closeCustomNote( )" ) Close

  .modal.fade( ref="customEditionModalRef" )
    .modal-dialog.modal-dialog-centered.modal-lg
      .modal-content
        .modal-header
          h5.modal-title Custom Script
        .modal-body
          .form-group.position-relative
            input.form-control( type="text"
                                id="customEditionUrl"
                                ref="customEditionUrlInput"
                                v-model="customEditionUrl"
                                placeholder="Search scripts or paste URL..."
                                @keydown="onSearchKeydown"
                                @blur="onSearchBlur" )
            .list-group.position-absolute.w-100( v-if="showSearchDropdown && (isSearching || searchResults.length > 0)"
                                                 style="z-index: 1060; top: 100%; max-height: 300px; overflow-y: auto; font-size: 1rem; line-height: 1rem" )
              .list-group-item.text-center( v-if="isSearching" )
                .spinner-border.spinner-border-sm.me-2
                | Searching...
              template( v-else )
                button.list-group-item.list-group-item-action.py-2( v-for="(result, idx) in searchResults"
                                                                    :key="result.id"
                                                                    type="button"
                                                                    :class="{ active: idx === selectedSearchIndex }"
                                                                    @click="chooseSearchItem(result)"
                                                                    @mouseenter="setSearchIndex(idx)" )
                  span {{ result.name }}
                  span.text-muted.small.ms-2( v-if="result.author" ) by {{ result.author }}
                  span.badge.bg-secondary.ms-2( v-if="result.type" ) {{ result.type }}
          .alert.alert-info.py-2.mt-2.mb-0( v-if="selectedSearchItem || isCustomEdition" style="font-size: 2rem; line-height: 2rem" )
            template( v-if="selectedSearchItem" )
              strong {{ selectedSearchItem.name }}
              span.ms-1( v-if="selectedSearchItem.author" ) by {{ selectedSearchItem.author }}
            template( v-else )
              strong {{ customEditionName }}
              span.ms-1( v-if="customEditionAuthor" ) by {{ customEditionAuthor }}
        .modal-footer
          button.btn.btn-primary( @click="applyCustomEdition( )" ) Apply
          button.btn.btn-link( @click="hideCustomEditionModal( )" ) Cancel

  .modal.fade( ref="timerDurationModalRef" )
    .modal-dialog.modal-dialog-centered
      .modal-content
        .modal-header
          h5.modal-title Select Timer Duration (minutes)
        .modal-body
          .row.text-center
            .col-4( v-for="num in 12" )
              button.btn.btn-lg.btn-outline-info.w-100.mb-2( @click="startTimer( num )" ) {{ num }}
        .modal-footer
          button.btn.btn-link( @click="hideTimerDurationModal( )" ) Cancel

  .modal.fade( ref="numberSelectionModalRef" )
    .modal-dialog.modal-dialog-centered
      .modal-content
        .modal-header
          h5.modal-title Select Number
        .modal-body
          .row.text-center
            .col-4
              button.btn.btn-lg.btn-outline-info.w-100.mb-2( @click="selectNumber( 0 )" ) 0
            .col-4( v-for="num in 8" )
              button.btn.btn-lg.btn-outline-info.w-100.mb-2( @click="selectNumber( num )" ) {{ num }}
        .modal-footer
          button.btn.btn-link( @click="hideNumberSelectionModal( )" ) Cancel

  .modal.fade.camera-modal( ref="cameraModalRef" data-bs-backdrop="static" data-bs-keyboard="false" )
    .modal-dialog.modal-dialog-centered.modal-fullscreen
      .modal-content
        .modal-header
          h5.modal-title Add Image
          .ms-auto.d-flex.gap-2
            button.btn.btn-lg.btn-primary.me-2( @click="triggerCameraCapture()" ) Take Photo
            button.btn.btn-lg.btn-secondary( @click="triggerGalleryPick()" ) Choose From Device
          input.d-none(type="file" ref="fileInputCamera" accept="image/*" capture="environment" @change="onFileChange")
          input.d-none(type="file" ref="fileInputGallery" accept="image/*" @change="onFileChange")
        .modal-body
          .row.text-center
            .col-12( v-if="!capturedImage" )
              .alert.alert-info Use Take Photo to open the camera, or Choose From Device to upload an existing image.
            .col-12( v-else )
              .captured-image-container.mb-3
                img.img-fluid( :src="capturedImage" :style="{ transform: `rotate(${imageRotation}deg)` }" )
        .modal-footer
          template( v-if="capturedImage" )
            button.btn.btn-lg.btn-outline-secondary( @click="sendImageToDisplay( )" ) Show
            button.btn.btn-lg.btn-outline-secondary( @click="capturedImage = null" ) Retake
            button.btn.btn-lg.btn-outline-secondary( @click="saveImage()" ) Save

            button.btn.btn-lg.btn-outline-secondary( @click="rotateImage( 'left' )" )
              font-awesome-icon( :icon="['fas', 'undo']" )
            button.btn.btn-lg.btn-outline-secondary( @click="rotateImage( 'right' )" )
              font-awesome-icon( :icon="['fas', 'redo']" )

          button.btn.btn-link( @click="hideCameraModal( )" ) Close

</template>

<style scoped>
  .connection-dot {
    position: fixed;
    top: 10px;
    right: 10px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    z-index: 1050;
  }
  .connection-dot.connected {
    background-color: #28a745;
  }
  .connection-dot.disconnected {
    background-color: #dc3545;
  }

  /* Responsive container for control buttons */
  .control-buttons-container {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 10px;
  }

  /* Make sure buttons don't get too small on mobile */
  .btn-group .btn {
    min-width: 40px;
  }

  /* Ensure the container allows for proper scrolling on mobile */
  .row {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  .modal-content {
    font-size: 5rem;
    line-height: 5rem;
  }

  /* Override font size for camera modal */
  .camera-modal .modal-content {
    font-size: 1rem;
    line-height: 1.5rem;
  }

  .image-container {
    align-items: center; /* Center image horizontally */
    position: relative; /* Positioning context for overlay */
  }

  .image-container img {
    text-align: center;
    max-width: 100%;
    height: auto;
  }

  .cards > label.btn, .cards > button.btn {
    line-height: .9rem !important;
  }

  /* Responsive styles for different screen sizes */
  @media (max-width: 576px) {
    .btn-group {
      width: 100%;
      justify-content: center;
    }

    .btn-group .btn {
      font-size: 0.85rem;
    }

    .cards.flex-wrap {
      justify-content: center;
    }
  }

  .captured-image-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    overflow: hidden;
  }

  .captured-image-container img {
    max-width: 100%;
    max-height: 60vh;
    object-fit: contain;
  }

</style>
